import React from "react";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import { Label, InputProps } from "reactstrap";

interface FormInputProps extends InputProps {
  name: string;
  label: string;
  error?: string;
  touched?: boolean;
  onChange?: any;
  isAsync?: boolean;
  formGroupClasses?: string;
  placeholder?: string;
  defaultFilter?: any;
  formatOptionLabel?: any;
}

const FormSelect: React.FC<FormInputProps> = ({
  id,
  name,
  label,
  value,
  error,
  touched,
  isMulti,
  isAsync,
  onChange,
  getOptions,
  options,
  formGroupClasses,
  placeholder,
  defaultFilter,
  formatOptionLabel,
  isClearable = false,
  getOptionLabel = (row: any) => row.name,
  getOptionValue = (row: any) => row.id,
}) => {
  // const [value, setValue] = useState<any>("");
  const resolvedPlaceholder = placeholder || label || "";
  const hasError = Boolean(touched && error);

  const selectStyles: any = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: 34,
      height: 34,
      borderRadius: 4,
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      borderBottomLeftRadius: 4,
      borderBottomRightRadius: 4,
      backgroundColor: state.isFocused ? "#fff" : "#f9fafb",
      borderColor: hasError ? "var(--field-error-border)" : state.isFocused ? "#4f46e5" : "#d1d5db",
      boxShadow: state.isFocused
        ? hasError
          ? "0 0 0 3px var(--field-error-ring)"
          : "0 0 0 3px rgba(79, 70, 229, 0.12)"
        : "none",
      "&:hover": {
        borderColor: hasError
          ? "var(--field-error-border)"
          : state.isFocused
          ? "#4f46e5"
          : "#d1d5db",
      },
    }),
    menu: (base: any) => ({
      ...base,
      borderRadius: 6,
      overflow: "hidden",
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: "0 8px",
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: 32,
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  async function loadOptions(
    search: any,
    loadedOptions: any,
    { page }: any,
    getOptions: any,
    defaultFilter: any,
  ) {
    const params = { search, page, limit: 10, ...defaultFilter };

    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value === null || value === undefined || value === "") {
        delete params[key];
      }
    });

    const response = await getOptions(params);
    return {
      options: response.data.results,
      hasMore: response.data.page < response.data.totalPages,
      additional: {
        page: page + 1,
      },
    };
  }

  return (
    <div className={formGroupClasses || "mb-3"}>
      {label && <Label htmlFor={name}>{label}</Label>}

      <div>
        {isAsync ? (
          <AsyncPaginate
            className={`react-select ${touched && error ? "is-invalid" : ""}`}
            classNamePrefix={"react-select"}
            styles={selectStyles}
            placeholder={resolvedPlaceholder}
            value={value}
            loadOptions={(search, loadedOptions, { page }: any) =>
              loadOptions(search, loadedOptions, { page }, getOptions, defaultFilter)
            }
            onChange={(value) => {
              onChange(name, value);
            }}
            additional={{
              page: 1,
            }}
            isClearable={isClearable}
            isMulti={isMulti}
            menuPortalTarget={document.body}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            formatOptionLabel={formatOptionLabel}
          />
        ) : (
          <Select
            id={id}
            name={name}
            className={`react-select ${touched && error ? "is-invalid" : ""}`}
            classNamePrefix={"react-select"}
            styles={selectStyles}
            placeholder={resolvedPlaceholder}
            value={value}
            options={options}
            onChange={(value) => {
              onChange(name, value);
            }}
            isClearable={isClearable}
            isMulti={isMulti}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            formatOptionLabel={formatOptionLabel}
          />
        )}
      </div>

      {touched && error && <small className='text-danger fw-medium'>{error}</small>}
    </div>
  );
};

export default FormSelect;
